import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import { arrayUnique } from 'common/utilities/core/array';
import theme, { sx } from 'core/utilities/theme';
import React from 'react';

import classes from './ToggleButton.style';

export interface ToggleButtonOption<T = string> {
  id: T;
  title?: string;
  content?: string | JSX.Element;
  /** Used when the variant is 'single' to highlight the selected option or not */
  active?: boolean;
}

export type ToggleButtonVariant = 'horizontal' | 'dropdown' | 'single';

export interface ComponentProps<T> {
  options: ToggleButtonOption<T>[];
  selectedOption: ToggleButtonOption<T>;
  /** Used within a dropdown to select multiple menu items */
  selectedOptions?: ToggleButtonOption<T>[];
  onChange: (option: ToggleButtonOption<T>) => void;
  /** Defaults to 'horizontal' */
  variant?: ToggleButtonVariant;
  itemsAnchor?: 'left' | 'right';
  disabled?: boolean;
  mr?: boolean;
  ml?: boolean;
  e2eId?: string;
  disabledTitle?: string;
}

function ToggleButton<T = string>({
  onChange, options, selectedOption, variant, itemsAnchor, disabled,
  ml, mr, e2eId, disabledTitle, selectedOptions,
}: ComponentProps<T>) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  if (variant === 'dropdown') {
    const selectedOptionsIds = arrayUnique([selectedOption.id, ...(selectedOptions || []).map((o) => o.id)]);

    return (
      <>
        <Tooltip
          title={selectedOption.title || ''}
          placement="top"
        >
          <Box sx={{ display: 'inline' }}>
            <ButtonGroup
              ref={anchorRef}
              color="primary"
              size="small"
              disabled={disabled}
              sx={sx(
                classes.buttonGroup,
                ml ? classes.ml : undefined,
                mr ? classes.mr : undefined,
              )}
            >
              <Button
                variant={selectedOption.active === false ? undefined : 'contained'}
                sx={typeof selectedOption.content !== 'string' ? classes.button : undefined}
                data-e2e-toggle-button={e2eId || ''}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen((prevOpen) => !prevOpen);
                }}
              >
                {selectedOption.content}
              </Button>
              <Button
                color="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen((prevOpen) => !prevOpen);
                }}
                style={{ padding: 0, minWidth: 0, marginLeft: 0 }}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
          </Box>
        </Tooltip>

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          placement={itemsAnchor === 'right' ? 'bottom-end' : 'bottom-start'}
          // see DropDown component
          sx={{ zIndex: 1301 }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
               
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList>
                    {options.map((option, i) => (option.id === 'separator' ? (
                       
                      <Divider data-testid="divider" key={`separator-${i}`} sx={{ m: theme.spacing(1, 0) }} />
                    ) : (
                      <MenuItem
                        key={`${option.id as unknown as string}`}
                        selected={selectedOptionsIds.includes(option.id)}
                        onClick={() => {
                          onChange(option);
                          setOpen(false);
                        }}
                        dense
                        title={option.title}
                        disabled={option.active === false}
                      >
                        {option.content}
                      </MenuItem>
                    )))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    );
  }

  let inlineOptions = options;
  if (variant === 'single') {
    if (disabled) {
      inlineOptions = [options[0]];
    } else {
      inlineOptions = options.filter((o) => o.id === selectedOption.id);
    }
  }

  const muiButtonVariant = (option: ToggleButtonOption<T>) => {
    if (variant === 'single') {
      return option.active ? 'contained' : 'outlined';
    }
    return selectedOption.id === option.id ? 'contained' : 'outlined';
  };

  return (
    <ButtonGroup
      color="primary"
      size="small"
      sx={sx(
        classes.buttonGroup,
        ml ? classes.ml : undefined,
        mr ? classes.mr : undefined,
      )}
      title={disabledTitle}
    >
      {inlineOptions.map((option) => (disabled ? (
        <Button
          key={`${option.id as unknown as string}`}
          variant={muiButtonVariant(option)}
          onClick={() => onChange(option)}
          sx={typeof option.content !== 'string' ? classes.button : undefined}
          disabled={disabled}
          data-e2e-toggle-button={e2eId || ''}
        >
          {option.content}
        </Button>
      ) : (
        <Tooltip
          key={`${option.id as unknown as string}`}
          title={option.title || ''}
          placement="top"
        >
          <Button
            variant={muiButtonVariant(option)}
            onClick={() => onChange(option)}
            sx={typeof option.content !== 'string' ? classes.button : undefined}
            disabled={disabled}
            data-e2e-toggle-button={e2eId || ''}
          >
            {option.content}
          </Button>
        </Tooltip>
      )))}
    </ButtonGroup>
  );
}

export default ToggleButton;
